/**
 * TransferTypeStep - Step 1: Seleção do tipo de transferência
 * 
 * Permite selecionar qual tipo de entidade será transferida:
 * - Conversas (assigned_to)
 * - Negócios - Responsável (assigned_to)
 * - Negócios - Vendedor (seller_id)
 * - Contatos - Vendedor (seller_id)
 */

import { Card, CardContent } from '@/components/ui/card';
import { 
  MessageSquare, 
  Briefcase, 
  UserCheck, 
  Users,
  Check,
  Building2
} from 'lucide-react';
import { 
  TransferType, 
  TRANSFER_TYPE_LABELS, 
  TRANSFER_TYPE_DESCRIPTIONS 
} from '@/hooks/useBulkTransfer';
import { cn } from '@/lib/utils';

// =============================================================================
// TIPOS
// =============================================================================

interface TransferTypeStepProps {
  /** Contexto de origem */
  context: 'queue' | 'deals';
  /** Se deve mostrar opções de CRM */
  showDealOptions: boolean;
  /** Tipo selecionado atualmente */
  selectedType: TransferType | null;
  /** Callback de seleção */
  onSelectType: (type: TransferType) => void;
}

// =============================================================================
// CONFIGURAÇÃO DOS TIPOS
// =============================================================================

interface TransferTypeConfig {
  type: TransferType;
  icon: React.ElementType;
  label: string;
  description: string;
  showInQueue: boolean;
  showInDeals: boolean;
  requiresCRM: boolean;
}

const TRANSFER_TYPES: TransferTypeConfig[] = [
  {
    type: 'conversations',
    icon: MessageSquare,
    label: TRANSFER_TYPE_LABELS.conversations,
    description: TRANSFER_TYPE_DESCRIPTIONS.conversations,
    showInQueue: true,
    showInDeals: false,
    requiresCRM: false,
  },
  {
    type: 'deals_assigned',
    icon: Briefcase,
    label: TRANSFER_TYPE_LABELS.deals_assigned,
    description: TRANSFER_TYPE_DESCRIPTIONS.deals_assigned,
    showInQueue: true,
    showInDeals: true,
    requiresCRM: true,
  },
  {
    type: 'deals_seller',
    icon: UserCheck,
    label: TRANSFER_TYPE_LABELS.deals_seller,
    description: TRANSFER_TYPE_DESCRIPTIONS.deals_seller,
    showInQueue: true,
    showInDeals: true,
    requiresCRM: true,
  },
  {
    type: 'contacts_seller',
    icon: Users,
    label: TRANSFER_TYPE_LABELS.contacts_seller,
    description: TRANSFER_TYPE_DESCRIPTIONS.contacts_seller,
    showInQueue: true,
    showInDeals: true,
    requiresCRM: true,
  },
  // v2.5.0: Novo tipo - Departamento (Conversa)
  {
    type: 'conversations_department',
    icon: Building2,
    label: TRANSFER_TYPE_LABELS.conversations_department,
    description: TRANSFER_TYPE_DESCRIPTIONS.conversations_department,
    showInQueue: true,
    showInDeals: false,
    requiresCRM: false,
  },
];

// =============================================================================
// COMPONENTE
// =============================================================================

export function TransferTypeStep({
  context,
  showDealOptions,
  selectedType,
  onSelectType,
}: TransferTypeStepProps) {
  // Filtrar tipos disponíveis baseado no contexto
  const availableTypes = TRANSFER_TYPES.filter((t) => {
    // Verificar contexto
    if (context === 'queue' && !t.showInQueue) return false;
    if (context === 'deals' && !t.showInDeals) return false;
    
    // Verificar se CRM está habilitado
    if (t.requiresCRM && !showDealOptions) return false;
    
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold">O que você deseja transferir?</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Selecione o tipo de registro para transferência em massa
        </p>
      </div>

      <div className="grid gap-3">
        {availableTypes.map((item) => {
          const Icon = item.icon;
          const isSelected = selectedType === item.type;

          return (
            <Card
              key={item.type}
              className={cn(
                'cursor-pointer transition-all hover:border-primary/50',
                isSelected && 'border-primary bg-primary/5 ring-1 ring-primary'
              )}
              onClick={() => onSelectType(item.type)}
            >
              <CardContent className="flex items-center gap-4 p-4">
                <div
                  className={cn(
                    'p-3 rounded-lg',
                    isSelected 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                
                <div className="flex-1">
                  <h4 className="font-medium">{item.label}</h4>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>

                {isSelected && (
                  <div className="p-1 rounded-full bg-primary text-primary-foreground">
                    <Check className="h-4 w-4" />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {availableTypes.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p>Nenhum tipo de transferência disponível.</p>
          {!showDealOptions && (
            <p className="text-sm mt-2">
              Habilite o módulo CRM para transferir negócios e contatos.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
